import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { RouteComponentProps, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FormikProps } from 'formik'

import ResetPasswordTemplate, { ResetPasswordTemplateProps } from '../../templates/ResetPassword'
import { useHeaderProps } from '../../components/Header/useProps'
import Header from '../../components/Header'
import { actions, selectors } from '../../redux'
import { router, routesPath } from '../../router'
import i18n from '../../i18n'
import { ResetPasswordFormValues } from '../../components/forms/ResetPasswordForm'
import { useSagaTakeEvery } from '../../hooks/useSaga'

const ResetPasswordPage: React.FC<RouteComponentProps> = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const history = useHistory()
  const routerParams = useParams<{ locale?: string; token: string; email: string }>()
  const match = useRouteMatch()

  // SELECTORS

  const resetPasswordService = useSelector(selectors.auth.resetPassword)

  // REF

  const $formikRef = useRef<FormikProps<ResetPasswordFormValues>>(null)

  // STATE

  const [languageSet, setLanguageSet] = useState(false)

  // PROPS

  const headerProps = useHeaderProps()

  const title = useMemo(() => {
    switch (match.path) {
      case routesPath.reset:
        return t('resetPassword.title')
      case routesPath.changePassword:
        return t('changePassword.title')
      default:
        return ''
    }
  }, [match.path, t])

  const templateProps: ResetPasswordTemplateProps = useMemo(
    () => ({
      layoutProps: {
        header: <Header {...headerProps} />,
      },
      title,
      successMessage: t('resetPassword.success'),
      backButton: {
        text: t('resetPassword.getBack'),
        onClick: () => {
          history.push(router(routesPath.root))
        },
      },
      form: {
        formik: {
          innerRef: $formikRef,
          validationSchema: Yup.object().shape({
            password: Yup.string().required().min(8),
            passwordConfirmation: Yup.string()
              .required()
              .oneOf([Yup.ref('password'), null]),
          }),
          initialValues: {
            password: '',
            passwordConfirmation: '',
          },
          onSubmit: () => {
            dispatch(
              actions.auth.resetPasswordRequest({
                email: routerParams.email,
                token: routerParams.token,
                password: $formikRef.current!.values.password!,
                password_confirmation: $formikRef.current!.values.passwordConfirmation!,
              })
            )
          },
        },
        title: 'title',
        text: t('resetPassword.form.description'),
        fields: {
          password: {
            name: 'password',
            label: t('resetPassword.form.password.label'),
            placeholder: t('resetPassword.form.password.placeholder'),
            required: true,
          },
          passwordConfirmation: {
            name: 'passwordConfirmation',
            label: t('resetPassword.form.passwordConfirm.label'),
            placeholder: t('resetPassword.form.passwordConfirm.placeholder'),
            required: true,
          },
        },
        submitErrors: resetPasswordService.errors,
        submitButton: {
          isPending: resetPasswordService.pending,
          isSuccess: resetPasswordService.success,
          text: t('resetPassword.form.button'),
        },
      },
    }),
    [
      headerProps,
      title,
      t,
      resetPasswordService.errors,
      resetPasswordService.pending,
      resetPasswordService.success,
      history,
      dispatch,
      routerParams.email,
      routerParams.token,
    ]
  )

  // EFFECT

  useEffect(() => {
    i18n.changeLanguage(routerParams.locale).then(() => {
      setLanguageSet(true)
    })
  }, [history, routerParams.locale])

  useEffect(() => {
    if (routerParams.token && routerParams.email) {
      dispatch(
        actions.auth.checkTokenRequest({
          email: routerParams.email,
          token: routerParams.token,
        })
      )
    }
  }, [dispatch, routerParams.email, routerParams.token])

  useSagaTakeEvery(
    actions.auth.checkTokenSuccess,
    useCallback(
      (action) => {
        if (action.payload === false) {
          history.push(router(routesPath.login, { locale: routerParams.locale ?? '' }))
        }
      },
      [history, routerParams.locale]
    )
  )

  // RETURN

  return <>{languageSet && <ResetPasswordTemplate {...templateProps} />}</>
}

export default ResetPasswordPage
